import type { ContentPrompt } from '~/models/Content/ContentPrompt'
import type { ChatMessage, ContentChatbot } from '~/models/Content/ContentChatbot'
import type { ChatbotOptions, PromptOptions, StreamMessage } from '~/models/Content/Chatbot'
import type { ClaudeModel, ClaudeModelId, LegacyClaudeModelId } from '~/models/Content/BaseField'
import { computed, ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import arrayUtils from '~/utils/arrayUtils'
import { ContentType } from '~/models/Content/ContentType'
import useContentApi from '~/api/contentApi'
import { getAccessToken } from '~/api/client/oidcClient'

const MAX_RECALL_DEFAULT = 15
const MAX_RECALL_LIMIT = 100

/**
 * Maps legacy model names to new API model IDs for backwards compatibility
 */
function mapToApiModel(model?: ClaudeModel): ClaudeModelId {
  const legacyMap: Record<LegacyClaudeModelId, ClaudeModelId> = {
    'sonnet': 'claude_haiku_4.5',
    'haiku': 'claude_haiku_4.5',
    'nova pro': 'nova_pro',
    'nova lite': 'nova_lite',
    'nova micro': 'nova_micro',
  }

  return legacyMap[model as LegacyClaudeModelId] ?? (model as ClaudeModelId) ?? 'claude_sonnet_4.5'
}

export default (options: ChatbotOptions) => {
  const { locationId } = { ...options }

  const { findContents } = useContentApi()
  const { truthy } = arrayUtils()

  const chatHistory = ref<ChatMessage[]>([])
  const isStreaming = ref(false)
  const isInteracting = ref(false)
  const isError = ref(false)

  const { data: bot, isLoading: isLoadingBot } = useQuery({
    enabled: !!locationId,
    queryKey: ['chatbot', locationId],
    queryFn: () => findContents<ContentChatbot>({
      locationIdCriterion: [locationId],
      contentTypeCriterion: [ContentType.Chatbot],
    }, 1).then(([data]) => data),
    staleTime: Infinity,
    retry: 1,
  })

  const relationIds = computed(() => [
    ...(bot.value?.promptStart?.destinationContentIds ?? []),
    ...(bot.value?.promptEnd?.destinationContentIds ?? []),
  ].filter(Boolean).map(Number))

  const { data: relations, isLoading: isLoadingRelations } = useQuery({
    enabled: computed(() => relationIds.value.length > 0),
    queryKey: computed(() => [...['prompts'], ...relationIds.value]),
    queryFn: async () => {
      const contents = await findContents<ContentPrompt>({
        contentIdCriterion: relationIds.value,
        contentTypeCriterion: [ContentType.Prompt],
        mainLocationCriterion: true,
      }, relationIds.value.length)

      return {
        start: (bot.value?.promptStart?.destinationContentIds ?? [])
          .map(id => contents.find(({ contentId }) => contentId === id))
          .filter(truthy<ContentPrompt>)
          .map(({ prompt }) => prompt)
          .join('\n\n'),
        end: (bot.value?.promptEnd?.destinationContentIds ?? [])
          .map(id => contents.find(({ contentId }) => contentId === id))
          .filter(truthy<ContentPrompt>)
          .map(({ prompt }) => prompt)
          .join('\n\n'),
      }
    },
    staleTime: Infinity,
  })

  const isLoading = computed(() => isLoadingBot.value || isLoadingRelations.value)

  const promptOptions = computed((): PromptOptions => ({
    prompt: [
      relations?.value?.start,
      bot?.value?.prompt,
      relations?.value?.end,
    ].filter(Boolean).join('\n\n'),
    model: bot?.value?.model,
    maxTokens: bot?.value?.maxTokens,
    topK: bot?.value?.topK,
    temperature: bot?.value?.temperature,
    maxRecall: bot?.value?.maxRecall,
  }))

  const defaultSystemInstruction = ' titlene dine må begynne på <h2> eller ##, aldri <h1> eller # .'

  const systemInstructions = computed(() => ({
    system: promptOptions.value.prompt + defaultSystemInstruction,
  }))

  async function* getIterableStream<T>(body: ReadableStream<Uint8Array>): AsyncIterable<T> {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    isStreaming.value = true
    while (true) {
      const { value, done } = await reader.read()
      if (done) {
        isStreaming.value = false
        break
      }
      const decoded = decoder.decode(value).split('\n')
      for (let i = 0; i < decoded.length; i++) {
        try {
          yield JSON.parse(decoded[i])
        } catch {
          // skip, likely not a complete JSON object yet
        }
      }
    }
  }

  const generateStream = async <T>(value: string, options: PromptOptions): Promise<AsyncIterable<T>> => {
    const apiModel = mapToApiModel(options.model)
    const maxRecall = Math.min(options.maxRecall || MAX_RECALL_DEFAULT, MAX_RECALL_LIMIT)
    const history = chatHistory.value.slice((Math.min(maxRecall, chatHistory.value.length) * -1))
      .map(m => ({ [m.from]: m.message }))

    const response = await fetch(`${import.meta.env.VITE_CORE_KI_API_URL}/queue/stream/chat`, {
      method: 'POST',
      headers: {
        'accept': 'application/x-ndjson',
        'Content-Type': 'application/json',
        'model': apiModel,
        'maxTokens': options.maxTokens?.toString() ?? '300',
        'topK': options.topK?.toString() ?? '100',
        'temperature': options.temperature?.toString() ?? '0.2',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify([systemInstructions.value, ...history, { user: value }]),
    })

    if (response.status !== 200)
      throw new Error(response.status.toString())
    if (!response.body)
      throw new Error('Response body does not exist')
    return getIterableStream<T>(response.body)
  }

  const prompt = async (value: string, initial = false) => {
    if (!value)
      return

    isInteracting.value = true

    if (initial && Number(bot.value?.opener?.length) > 0) {
      chatHistory.value = [...chatHistory.value, { from: 'assistant', message: bot.value?.opener ?? '' }]
      isInteracting.value = false
      return
    }

    let stream: AsyncIterable<StreamMessage>

    try {
      stream = await generateStream<StreamMessage>(value, promptOptions.value)
    } catch (error) {
      isError.value = true
      throw error
    } finally {
      isInteracting.value = false
      isError.value = false
    }

    if (initial) {
      chatHistory.value = [...chatHistory.value, { from: 'assistant', message: '' }]
    } else {
      chatHistory.value = [...chatHistory.value, { from: 'user', message: value }, { from: 'assistant', message: '' }]
    }

    for await (const chunk of stream) {
      chatHistory.value = chatHistory.value.map((m, i) => {
        if (i === chatHistory.value.length - 1)
          m.message += chunk?.text ?? ''
        return m
      })
    }
  }

  return {
    bot,
    relations,
    isLoading,
    isError,
    isStreaming,
    chatHistory,
    prompt,
    isInteracting,
    promptOptions,
  }
}
